import {
  DECISION_ENGINES,
  DECISION_SCOPES,
} from "../models";
import { decisionMatterAdapter } from "./decisionMatterAdapter";

const SOURCES = Object.freeze({
  portfolio_decision: {
    engine: DECISION_ENGINES.PORTFOLIO,
    scope: DECISION_SCOPES.PORTFOLIO,
  },
  risk_financing: {
    engine: DECISION_ENGINES.RISK_FINANCING,
    scope: DECISION_SCOPES.FINANCING,
  },
  scenario_decision: {
    engine: DECISION_ENGINES.SCENARIO,
    scope: DECISION_SCOPES.SCENARIO,
  },
});

function isGovernanceRecommendation(recommendation) {
  const contract = SOURCES[recommendation?.source];

  return Boolean(
    contract &&
    recommendation?.engine === contract.engine &&
    recommendation?.scope === contract.scope
  );
}

const alternativeLabel = (alternative) => {
  if (alternative == null) return null;
  if (typeof alternative !== "object") return String(alternative);
  return alternative.id ?? alternative.strategy ?? alternative.strategy_name ??
    alternative.name ?? alternative.title ?? alternative.label ?? null;
};

function resolveRecommendedAlternative(recommendation, alternatives) {
  const metadata = recommendation.metadata || {};
  const candidate = metadata.recommendedAlternative ??
    recommendation.selectedAlternative ?? recommendation.strategy ?? null;
  const candidateLabel = alternativeLabel(candidate);

  return alternatives.find(
    (alternative) => alternativeLabel(alternative) === candidateLabel
  ) ?? candidate;
}

/*
 * Population is deliberately a boundary operation. It accepts only canonical
 * recommendations produced by the three decision engines; raw FAIR results
 * are evidence and cannot become governance matters on their own.
 */
export function populateDecisionMatters(recommendations = []) {
  if (!Array.isArray(recommendations)) {
    return [];
  }

  return recommendations
    .filter(isGovernanceRecommendation)
    .map((recommendation) => {
      const metadata = recommendation.metadata || {};
      const evidenceAlternatives = Array.isArray(recommendation.evidence?.alternatives)
        ? recommendation.evidence.alternatives
        : [];
      const allAlternatives = Array.isArray(metadata.alternatives)
        ? metadata.alternatives
        : evidenceAlternatives;
      const recommendedAlternative = resolveRecommendedAlternative(
        recommendation,
        allAlternatives
      );
      const recommendedLabel = alternativeLabel(recommendedAlternative);

      return decisionMatterAdapter(recommendation, {
        id: recommendation.id == null
          ? null
          : `DM-${recommendation.id}`,
        businessQuestion: metadata.businessQuestion ?? null,
        decisionRequired: metadata.decisionRequired ?? null,
        recommendedAlternative,
        alternatives: allAlternatives.filter(
          (alternative) => alternativeLabel(alternative) !== recommendedLabel
        ),
        sourceRecommendation: metadata.sourceRecommendation ?? recommendation,
        createdFromRecommendationId:
          metadata.createdFromRecommendationId ?? recommendation.id ?? null,
        sourceReference: metadata.sourceReference ?? null,
        metadata: {
          provenance: {
            recommendationId: recommendation.id ?? null,
            source: recommendation.source,
            engine: recommendation.engine,
            scope: recommendation.scope,
          },
        },
      });
    });
}

export default populateDecisionMatters;
